import * as React from 'react';
import { useContext } from 'react';
import { Collapse } from '@mui/material';
import { useTheme } from '../../../../theme';
import { useTranslation } from 'react-i18next';
import { DisabilitiesContext } from './context';
import { useToggleable } from '../../../../hooks';
import { Field, useFormState } from 'react-final-form';
import { Switch } from '@shared/ui';
import List from './List/List';
import { validateDisabilities } from './utils';
import cn from 'classnames';
import { FormValues } from '../../../../Checkout/types';

interface Props {
	groupIcon?: React.ReactNode;
}

const Disabilities: React.FC<Props> = ({ groupIcon }) => {
	const theme = useTheme('PassengerForm').Disabilities;
	const groupTheme = useTheme('PassengerForm').Group;
	const { t } = useTranslation('Passenger');

	const { availableDisabilities, passengerId } = useContext(DisabilitiesContext);
	const {
		values: { passengers },
		errors,
		submitFailed
	} = useFormState();

	const disabilities = passengers[passengerId].disabilities;
	const { isOpen, toggle } = useToggleable(disabilities?.enabled);

	const fieldNameBasePath = `passengers.${passengerId}.disabilities`;

	if (!availableDisabilities?.length) {
		return null;
	}

	return (
		<div className={theme.container}>
			<Field
				name={`${fieldNameBasePath}.enabled`}
				initialValue={isOpen}
				type={'checkbox'}
				validate={(value, allValues) => {
					const formValues = allValues as FormValues;
					const error = validateDisabilities(formValues.passengers?.[passengerId]);

					return error ? t(error) : null;
				}}
				render={({ input, meta }) => (
					<div className={theme.switchWrapper}>
						{groupIcon && <div className={cn(groupTheme.icon, theme.icon)}>{groupIcon}</div>}

						<Switch
							classes={{
								root: theme.switch__root
							}}
							checked={isOpen}
							onChange={() => {
								toggle();
								input.onChange({
									...new Event('change'),
									target: {
										value: !isOpen
									}
								});
							}}
							label={t('Passenger with disabilities')}
							labelPosition="right"
						/>

						{meta.submitFailed && meta.error && (
							<div className={theme.switchControl__errorText}>{meta.error}</div>
						)}
					</div>
				)}
			/>

			<Collapse in={isOpen}>
				<List
					availableDisabilities={availableDisabilities}
					passengerId={passengerId}
					passengerDisabilitiesState={disabilities}
					fieldNameBasePath={fieldNameBasePath}
					isError={submitFailed && !!errors['passengers']?.[passengerId]?.['disabilities']}
				/>
			</Collapse>
		</div>
	);
};

export default Disabilities;
